import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import TrainerIntakeEbayReconciliationPanel from "./TrainerIntakeEbayReconciliationPanel";
import TrainerAllocationDecisionPanel from "./TrainerAllocationDecisionPanel";
import {
  applyTrainerIntakeEbayProjection,
  createTrainerIntakeBatch,
  createTrainerIntakeRow,
  fetchTrainerIntakeBatches,
  fetchTrainerIntakeRows,
  finaliseTrainerIntakeBatch,
  fetchTrainerAllocationContext,
  refreshTrainerAllocationStoreQty,
  refreshTrainerIntakeEbayPreview,
} from "./trainerInventoryApi";

const TRAINER_LISTING_STRATEGIES = [
  { value: "trainer_single", label: "Trainer Single" },
  { value: "trainer_playset", label: "Trainer Playset" },
  { value: "trainer_both", label: "Trainer Both" },
  { value: "trainer_bulk", label: "Trainer Bulk" },
];

const SOURCE_TYPES = [
  { value: "bulk_lot", label: "Bulk Lot" },
  { value: "standard", label: "Standard" },
  { value: "pack_rip", label: "Pack Rip" },
  { value: "targeted_buy", label: "Targeted Buy" },
];

const APPLY_MODES = [
  { value: "stage", label: "Stage For Finalise Stock" },
  { value: "apply_immediately", label: "Receive Stock Immediately" },
];

const CARD_VARIANT_OPTIONS = ["Normal", "Reverse Holo", "Holo"];

const READY_RECONCILIATION_STATUSES = new Set(["ready_to_update", "ready_to_relist"]);
const SUCCESS_RECONCILIATION_STATUSES = new Set(["updated_successfully", "relisted_successfully"]);

const buildRowDraft = () => ({
  card_name: "",
  card_variant: "Normal",
  quantity: 1,
  single_allocation_quantity: 0,
  playset_allocation_quantity: 0,
  card_condition: "",
  listing_strategy: "trainer_single",
  source_type: "bulk_lot",
  unit_cost: "",
  source_reference: "",
  storage_location: "",
  notes: "",
  inventory_apply_mode: "stage",
});

const parseQuantity = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const allocationForMode = (quantityValue, mode) => {
  const quantity = Math.max(Math.trunc(parseQuantity(quantityValue, 0)), 0);
  if (mode === "all_singles") {
    return { single_allocation_quantity: quantity, playset_allocation_quantity: 0 };
  }
  if (mode === "max_playsets") {
    return { single_allocation_quantity: 0, playset_allocation_quantity: Math.floor(quantity / 4) };
  }
  if (mode === "playsets_with_remainder") {
    return {
      single_allocation_quantity: quantity % 4,
      playset_allocation_quantity: Math.floor(quantity / 4),
    };
  }
  return { single_allocation_quantity: 0, playset_allocation_quantity: 0 };
};

const allocationMetrics = (quantityValue, singleValue, playsetValue) => {
  const quantity = Math.trunc(parseQuantity(quantityValue, 0));
  const singles = Math.trunc(parseQuantity(singleValue, 0));
  const playsets = Math.trunc(parseQuantity(playsetValue, 0));
  const allocated = singles + playsets * 4;
  return {
    quantity,
    singles,
    playsets,
    allocated,
    unallocated: Math.max(quantity - allocated, 0),
    valid: quantity > 0 && singles >= 0 && playsets >= 0 && allocated <= quantity,
  };
};

const parsePasteLine = (line, index) => {
  const trimmed = line.trim();
  if (!trimmed) {
    return null;
  }

  const parts = (trimmed.includes("\t") ? trimmed.split("\t") : trimmed.split(",")).map((part) => part.trim());
  const maybePlaysets = Number(parts[parts.length - 1]);
  const maybeSingles = Number(parts[parts.length - 2]);
  const maybeQuantity = Number(parts[parts.length - 3]);
  const hasExplicitAllocation =
    parts.length >= 4 &&
    Number.isInteger(maybeQuantity) &&
    Number.isInteger(maybeSingles) &&
    Number.isInteger(maybePlaysets);

  if (hasExplicitAllocation) {
    const cardName = parts.slice(0, -3).join(",").trim();
    const metrics = allocationMetrics(maybeQuantity, maybeSingles, maybePlaysets);
    return {
      id: `${index}-${trimmed}`,
      raw: trimmed,
      card_name: cardName,
      quantity: maybeQuantity,
      single_allocation_quantity: maybeSingles,
      playset_allocation_quantity: maybePlaysets,
      explicit_allocation: true,
      valid: Boolean(cardName) && metrics.valid,
    };
  }

  const quantity = Number(parts[parts.length - 1]);
  const cardName = parts.slice(0, -1).join(",").trim();
  return {
    id: `${index}-${trimmed}`,
    raw: trimmed,
    card_name: cardName,
    quantity: Number.isInteger(quantity) ? quantity : 0,
    explicit_allocation: false,
    valid: Boolean(cardName) && Number.isInteger(quantity) && quantity > 0,
  };
};

const reconciliationKey = (rowId, listingRole) => `${rowId}:${listingRole}`;


const statusLabel = (row) => {
  if (row.match_status === "matched_existing") {
    return "Matched";
  }
  if (row.match_status === "created_new") {
    return "New";
  }
  return row.match_status || "-";
};

const isReadyReconciliationStatus = (status) => READY_RECONCILIATION_STATUSES.has(status);

const normalizeApplyStatus = (status) => {
  if (status === "relisted" || status === "relisted_successfully") {
    return "relisted_successfully";
  }
  if (status === "pushed" || status === "updated_successfully") {
    return "updated_successfully";
  }
  if (status === "relist_failed") {
    return "relist_failed";
  }
  if (status === "push_failed" || status === "update_failed") {
    return "update_failed";
  }
  return status || "updated_successfully";
};

const readyActionName = (status) => (status === "ready_to_relist" ? "relist" : "update");

export default function TrainerBulkIntake({ onEbayErrorLogged = () => {} }) {
  const [batches, setBatches] = useState([]);
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [rows, setRows] = useState([]);
  const [batchDraft, setBatchDraft] = useState({
    batch_name: "",
    source_label: "",
    notes: "",
  });
  const [rowDraft, setRowDraft] = useState(buildRowDraft);
  const [pasteText, setPasteText] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [savingBulk, setSavingBulk] = useState(false);
  const [lookupOpen, setLookupOpen] = useState(false);
  const [lookupQuery, setLookupQuery] = useState("");
  const [lookupResults, setLookupResults] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [selectedRowId, setSelectedRowId] = useState("");
  const [reconciliationByRowId, setReconciliationByRowId] = useState({});
  const [reconciliationActions, setReconciliationActions] = useState({
    refreshing: {},
    approving: {},
    applyAll: false,
  });
  const [singleDraftStatusByRowId, setSingleDraftStatusByRowId] = useState({});
  const [playsetDraftStatusByRowId, setPlaysetDraftStatusByRowId] = useState({});
  const [pasteAllocationMode, setPasteAllocationMode] = useState("unallocated");
  const [allocationContext, setAllocationContext] = useState(null);
  const [allocationContextLoading, setAllocationContextLoading] = useState(false);
  const [allocationContextError, setAllocationContextError] = useState("");
  const [allocationStoreQty, setAllocationStoreQty] = useState(null);
  const [allocationStoreQtyRefreshing, setAllocationStoreQtyRefreshing] = useState(false);

  const selectedBatch = useMemo(
    () => batches.find((batch) => String(batch.id) === String(selectedBatchId)) || null,
    [batches, selectedBatchId]
  );

  const parsedPasteRows = useMemo(
    () =>
      pasteText
        .split(/\r?\n/)
        .map(parsePasteLine)
        .filter(Boolean)
        .map((row) => {
          const allocation = row.explicit_allocation
            ? {
                single_allocation_quantity: row.single_allocation_quantity,
                playset_allocation_quantity: row.playset_allocation_quantity,
              }
            : allocationForMode(row.quantity, pasteAllocationMode);
          const metrics = allocationMetrics(
            row.quantity,
            allocation.single_allocation_quantity,
            allocation.playset_allocation_quantity
          );
          return { ...row, ...allocation, ...metrics, valid: Boolean(row.card_name) && metrics.valid };
        }),
    [pasteText, pasteAllocationMode]
  );

  const validPasteRows = useMemo(
    () => parsedPasteRows.filter((row) => row.valid),
    [parsedPasteRows]
  );

  const pasteHasInvalidRows = useMemo(
    () => parsedPasteRows.some((row) => !row.valid),
    [parsedPasteRows]
  );

  const rowDraftAllocation = useMemo(
    () =>
      allocationMetrics(
        rowDraft.quantity,
        rowDraft.single_allocation_quantity,
        rowDraft.playset_allocation_quantity
      ),
    [rowDraft.quantity, rowDraft.single_allocation_quantity, rowDraft.playset_allocation_quantity]
  );

  const rowTotals = useMemo(
    () => ({
      rows: rows.length,
      quantity: rows.reduce((sum, row) => sum + parseQuantity(row.quantity, 0), 0),
      applied: rows.reduce((sum, row) => sum + parseQuantity(row.quantity_applied, 0), 0),
      matched: rows.filter((row) => row.match_status === "matched_existing").length,
      created: rows.filter((row) => row.match_status === "created_new").length,
    }),
    [rows]
  );

  const loadBatches = async (nextSelectedBatchId = selectedBatchId) => {
    try {
      const response = await fetchTrainerIntakeBatches();
      const nextBatches = response.data || [];
      setBatches(nextBatches);
      if (!nextSelectedBatchId && nextBatches[0]?.id) {
        setSelectedBatchId(nextBatches[0].id);
        return nextBatches[0].id;
      }
      return nextSelectedBatchId;
    } catch (error) {
      setErrorMessage(error.response?.data?.error || "Failed to load intake batches");
      return nextSelectedBatchId;
    }
  };

  const loadRows = async (batchId = selectedBatchId) => {
    if (!batchId) {
      setRows([]);
      setSelectedRowId("");
      setReconciliationByRowId({});
      return;
    }
    try {
      const response = await fetchTrainerIntakeRows(batchId);
      const nextRows = response.data?.rows || [];
      const nextRowIds = new Set(nextRows.map((row) => String(row.id)));
      setRows(nextRows);
      setReconciliationByRowId((prev) =>
        Object.fromEntries(
          Object.entries(prev).filter(([actionKey]) => nextRowIds.has(String(actionKey).split(":")[0]))
        )
      );
      setSelectedRowId((prev) => {
        if (prev && nextRows.some((row) => String(row.id) === String(prev))) {
          return prev;
        }
        return nextRows[0]?.id || "";
      });
    } catch (error) {
      setErrorMessage(error.response?.data?.error || "Failed to load intake rows");
    }
  };

  useEffect(() => {
    loadBatches().then((batchId) => {
      if (batchId) {
        loadRows(batchId);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!lookupOpen) {
      return undefined;
    }
    const query = lookupQuery.trim();
    if (query.length < 2) {
      setLookupResults([]);
      setLookupLoading(false);
      setLookupError("");
      return undefined;
    }

    setLookupLoading(true);
    setLookupError("");
    const timeoutId = setTimeout(async () => {
      try {
        const response = await axios.get("/api/cards/lookup", {
          params: {
            q: query,
            limit: 24,
            high_only: 0,
          },
        });
        setLookupResults(response.data?.data || []);
      } catch (error) {
        setLookupResults([]);
        setLookupError(error.response?.data?.error || "Card lookup failed");
      } finally {
        setLookupLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [lookupOpen, lookupQuery]);

  useEffect(() => {
    const cardName = (rowDraft.card_name || "").trim();
    if (cardName.length < 2) {
      setAllocationContext(null);
      setAllocationContextLoading(false);
      setAllocationContextError("");
      setAllocationStoreQty(null);
      return undefined;
    }

    let cancelled = false;
    setAllocationContextLoading(true);
    setAllocationContextError("");
    setAllocationStoreQty(null);
    const timeoutId = setTimeout(async () => {
      try {
        const response = await fetchTrainerAllocationContext({
          card_name: cardName,
          card_variant: rowDraft.card_variant || "Normal",
          incoming_quantity: Math.max(Math.trunc(parseQuantity(rowDraft.quantity, 0)), 0),
        });
        if (!cancelled) {
          setAllocationContext(response.data || null);
        }
      } catch (error) {
        if (!cancelled) {
          setAllocationContext(null);
          setAllocationContextError(error.response?.data?.error || "Allocation context lookup failed");
        }
      } finally {
        if (!cancelled) {
          setAllocationContextLoading(false);
        }
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [rowDraft.card_name, rowDraft.card_variant, rowDraft.quantity]);

  const refreshSelectedBatch = async (batchId = selectedBatchId) => {
    const nextBatchId = await loadBatches(batchId);
    await loadRows(nextBatchId);
  };

  const updateBatchDraft = (field, value) => {
    setBatchDraft((prev) => ({ ...prev, [field]: value }));
  };

  const updateRowDraft = (field, value) => {
    setRowDraft((prev) => ({ ...prev, [field]: value }));
  };

  const applyRowAllocationMode = (mode) => {
    setRowDraft((prev) => ({ ...prev, ...allocationForMode(prev.quantity, mode) }));
  };

  const openLookup = () => {
    setLookupQuery(rowDraft.card_name || "");
    setLookupResults([]);
    setLookupError("");
    setLookupOpen(true);
  };

  const closeLookup = () => {
    setLookupOpen(false);
    setLookupLoading(false);
    setLookupError("");
  };

  const selectLookupCard = (card) => {
    const selectedName = card?.name_and_number || card?.card_name || "";
    if (selectedName) {
      updateRowDraft("card_name", selectedName);
      setStatusMessage(`Selected ${selectedName}`);
    }
    closeLookup();
  };

  const useAllocationSuggestion = () => {
    const suggestion = allocationContext?.suggestion;
    if (!suggestion) {
      return;
    }
    setRowDraft((prev) => ({
      ...prev,
      single_allocation_quantity: Math.max(Math.trunc(parseQuantity(suggestion.suggested_single_allocation, 0)), 0),
      playset_allocation_quantity: Math.max(Math.trunc(parseQuantity(suggestion.suggested_playset_allocation, 0)), 0),
    }));
    setStatusMessage("Applied the store-demand suggestion to the unsaved allocation fields");
  };

  const refreshAllocationStoreQty = async () => {
    if (allocationContext?.match_status !== "matched_existing") {
      return;
    }
    setAllocationStoreQtyRefreshing(true);
    try {
      const response = await refreshTrainerAllocationStoreQty({
        inventory_id: allocationContext.inventory_id,
        card_name: allocationContext.card_name,
        card_variant: allocationContext.card_variant,
        incoming_quantity: Math.max(Math.trunc(parseQuantity(rowDraft.quantity, 0)), 0),
      });
      const refreshPayload = response.data || null;
      setAllocationStoreQty(refreshPayload);
      if (refreshPayload?.context) {
        setAllocationContext(refreshPayload.context);
      }
    } catch (error) {
      setAllocationStoreQty({
        error: error.response?.data?.error || "Live store quantity refresh failed",
        roles: {},
      });
    } finally {
      setAllocationStoreQtyRefreshing(false);
    }
  };

  const createBatch = async () => {
    setErrorMessage("");
    setStatusMessage("");
    try {
      const response = await createTrainerIntakeBatch(batchDraft);
      const createdBatch = response.data?.batch;
      setBatchDraft({ batch_name: "", source_label: "", notes: "" });
      if (createdBatch?.id) {
        setSelectedBatchId(createdBatch.id);
        setStatusMessage(`Created batch ${createdBatch.batch_name}`);
        await refreshSelectedBatch(createdBatch.id);
      }
    } catch (error) {
      setErrorMessage(error.response?.data?.error || "Failed to create intake batch");
    }
  };

  const createRowPayload = (overrides = {}) => ({
    ...rowDraft,
    quantity: Math.trunc(parseQuantity(rowDraft.quantity, 0)),
    single_allocation_quantity: Math.trunc(parseQuantity(rowDraft.single_allocation_quantity, 0)),
    playset_allocation_quantity: Math.trunc(parseQuantity(rowDraft.playset_allocation_quantity, 0)),
    unit_cost: rowDraft.unit_cost === "" ? undefined : rowDraft.unit_cost,
    ...overrides,
  });

  const saveSingleRow = async () => {
    if (!selectedBatchId) {
      setErrorMessage("Select or create an intake batch first");
      return;
    }
    if (!rowDraftAllocation.valid) {
      setErrorMessage("Allocation must fit within received quantity: singles + (playsets x 4) cannot exceed quantity");
      return;
    }
    setErrorMessage("");
    setStatusMessage("");
    try {
      const response = await createTrainerIntakeRow(selectedBatchId, createRowPayload());
      const createdRow = response.data?.row;
      setRowDraft(buildRowDraft());
      if (createdRow?.id) {
        setSelectedRowId(createdRow.id);
      }
      setStatusMessage(
        `Saved ${createdRow?.card_name || "row"} as ${statusLabel(createdRow || {})}`
      );
      await refreshSelectedBatch(selectedBatchId);
    } catch (error) {
      setErrorMessage(error.response?.data?.error || "Failed to save intake row");
    }
  };

  const savePasteRows = async () => {
    if (!selectedBatchId) {
      setErrorMessage("Select or create an intake batch first");
      return;
    }
    if (validPasteRows.length === 0) {
      setErrorMessage("Paste at least one valid line in Card Name, Quantity or Card Name, Quantity, Singles, Playsets format");
      return;
    }
    if (pasteHasInvalidRows) {
      setErrorMessage("Fix all invalid pasted rows before saving. No pasted rows were saved.");
      return;
    }
    setSavingBulk(true);
    setErrorMessage("");
    setStatusMessage("");
    try {
      let lastCreatedRowId = "";
      for (const row of validPasteRows) {
        const response = await createTrainerIntakeRow(
          selectedBatchId,
          createRowPayload({
            card_name: row.card_name,
            quantity: row.quantity,
            single_allocation_quantity: row.single_allocation_quantity,
            playset_allocation_quantity: row.playset_allocation_quantity,
            notes: rowDraft.notes,
          })
        );
        if (response.data?.row?.id) {
          lastCreatedRowId = response.data.row.id;
        }
      }
      if (lastCreatedRowId) {
        setSelectedRowId(lastCreatedRowId);
      }
      setStatusMessage(`Saved ${validPasteRows.length} pasted rows`);
      setPasteText("");
      await refreshSelectedBatch(selectedBatchId);
    } catch (error) {
      setErrorMessage(error.response?.data?.error || "Failed while saving pasted rows");
      await refreshSelectedBatch(selectedBatchId);
    } finally {
      setSavingBulk(false);
    }
  };

  const finaliseBatch = async () => {
    if (!selectedBatchId) {
      return;
    }
    setErrorMessage("");
    setStatusMessage("");
    try {
      const response = await finaliseTrainerIntakeBatch(selectedBatchId);
      setStatusMessage(
        response.data?.already_finalised
          ? "Batch stock was already finalised"
          : `Finalised stock for ${response.data?.rows_applied || 0} rows`
      );
      await refreshSelectedBatch(selectedBatchId);
    } catch (error) {
      setErrorMessage(error.response?.data?.error || "Failed to finalise intake stock");
    }
  };

  const createSingleDraftForRow = async (row, event) => {
    if (event) {
      event.stopPropagation();
    }
    if (!row.trainer_inventory_summary_id || !row.inventory_applied_at) {
      setErrorMessage("Finalise Stock before creating a single listing draft");
      return;
    }
    const draftQuantity = parseQuantity(row.single_allocation_quantity, 0);
    if (draftQuantity <= 0) {
      setErrorMessage("This intake row has no single allocation to draft");
      return;
    }
    setErrorMessage("");
    setStatusMessage("");
    setSingleDraftStatusByRowId((prev) => ({ ...prev, [row.id]: "saving" }));
    try {
      const response = await axios.post(
        `/api/trainer_inventory/${row.trainer_inventory_summary_id}/create_single_draft`,
        { quantity: draftQuantity }
      );
      const linkedListingId = response.data?.linked_listing_id || response.data?.row?.linked_listing_id;
      setSingleDraftStatusByRowId((prev) => ({ ...prev, [row.id]: "saved" }));
      setStatusMessage(
        linkedListingId
          ? `Single draft ready for ${row.card_name} as listing #${linkedListingId}`
          : `Single draft action completed for ${row.card_name}`
      );
      await refreshSelectedBatch(selectedBatchId);
    } catch (error) {
      const message = error.response?.data?.error || "Failed to create single draft";
      setSingleDraftStatusByRowId((prev) => ({ ...prev, [row.id]: message }));
      setErrorMessage(message);
    }
  };

  const createPlaysetDraftForRow = async (row, event) => {
    if (event) {
      event.stopPropagation();
    }
    if (!row.trainer_inventory_summary_id || !row.inventory_applied_at) {
      setErrorMessage("Finalise Stock before creating a playset listing draft");
      return;
    }
    if (parseQuantity(row.playset_allocation_quantity, 0) <= 0) {
      setErrorMessage("This intake row has no playset allocation to draft");
      return;
    }
    const draftQuantity = parseQuantity(row.playset_allocation_quantity, 0);
    setErrorMessage("");
    setStatusMessage("");
    setPlaysetDraftStatusByRowId((prev) => ({ ...prev, [row.id]: "saving" }));
    try {
      const response = await axios.post(
        `/api/trainer_inventory/${row.trainer_inventory_summary_id}/create_playset_draft`,
        { quantity: draftQuantity }
      );
      const linkedListingId = response.data?.linked_playset_listing_id || response.data?.row?.linked_playset_listing_id;
      setPlaysetDraftStatusByRowId((prev) => ({ ...prev, [row.id]: "saved" }));
      setStatusMessage(
        linkedListingId
          ? `Playset draft ready for ${row.card_name} as listing #${linkedListingId}`
          : `Playset draft action completed for ${row.card_name}`
      );
      await refreshSelectedBatch(selectedBatchId);
    } catch (error) {
      const message = error.response?.data?.error || "Failed to create playset draft";
      setPlaysetDraftStatusByRowId((prev) => ({ ...prev, [row.id]: message }));
      setErrorMessage(message);
    }
  };

  const handleBatchSelect = async (batchId) => {
    setSelectedBatchId(batchId);
    setErrorMessage("");
    setStatusMessage("");
    setSelectedRowId("");
    setReconciliationByRowId({});
    await loadRows(batchId);
  };

  const setRowAction = (actionKey, rowId, listingRole, value) => {
    const key = reconciliationKey(rowId, listingRole);
    setReconciliationActions((prev) => ({
      ...prev,
      [actionKey]: {
        ...prev[actionKey],
        [key]: value,
      },
    }));
  };

  const refreshReconciliationRow = async (rowId, listingRole, options = {}) => {
    if (!rowId || !listingRole) {
      return null;
    }
    const key = reconciliationKey(rowId, listingRole);
    setErrorMessage("");
    if (!options.silent) {
      setStatusMessage("");
    }
    setSelectedRowId(rowId);
    setRowAction("refreshing", rowId, listingRole, true);
    try {
      const response = await refreshTrainerIntakeEbayPreview(rowId, listingRole);
      const projection = response.data || {};
      setReconciliationByRowId((prev) => ({ ...prev, [key]: projection }));
      if (!options.silent) {
        setStatusMessage(
          isReadyReconciliationStatus(projection.status)
            ? `${projection.status === "ready_to_relist" ? "Relist" : "Update"} preview ready for ${listingRole} ${projection.card_name || `row #${rowId}`}`
            : `${projection.card_name || `Row #${rowId}`} ${listingRole}: ${projection.message || projection.status || "Review required"}`
        );
      }
      if (projection.status && !isReadyReconciliationStatus(projection.status) && !["no_allocation"].includes(projection.status)) {
        onEbayErrorLogged();
      }
      await refreshSelectedBatch(selectedBatchId);
      return projection;
    } catch (error) {
      const projection = {
        listing_role: listingRole,
        status: "lookup_failed",
        message: error.response?.data?.error || "Failed to refresh eBay preview",
      };
      setReconciliationByRowId((prev) => ({ ...prev, [key]: projection }));
      if (!options.silent) {
        setErrorMessage(projection.message);
      }
      onEbayErrorLogged();
      return projection;
    } finally {
      setRowAction("refreshing", rowId, listingRole, false);
    }
  };

  const approveReconciliationRow = async (rowId, listingRole, options = {}) => {
    const key = reconciliationKey(rowId, listingRole);
    const readyStatus = reconciliationByRowId[key]?.status;
    if (!rowId || !listingRole || !isReadyReconciliationStatus(readyStatus)) {
      return null;
    }
    setErrorMessage("");
    if (!options.silent) {
      setStatusMessage("");
    }
    setSelectedRowId(rowId);
    setRowAction("approving", rowId, listingRole, true);
    try {
      const response = await applyTrainerIntakeEbayProjection(rowId, listingRole);
      const normalisedStatus = normalizeApplyStatus(response.data?.status);
      const projection = {
        ...(response.data || {}),
        listing_role: listingRole,
        status: normalisedStatus,
      };
      setReconciliationByRowId((prev) => ({ ...prev, [key]: projection }));
      if (!options.silent) {
        setStatusMessage(
          normalisedStatus === "relisted_successfully"
            ? `Relisted existing ${listingRole} eBay offer for ${projection.card_name || `row #${rowId}`}`
            : `Updated ${listingRole} eBay quantity for ${projection.card_name || `row #${rowId}`}`
        );
      }
      await refreshSelectedBatch(selectedBatchId);
      onEbayErrorLogged();
      return projection;
    } catch (error) {
      const failedStatus = normalizeApplyStatus(error.response?.data?.status);
      const projection = {
        ...(error.response?.data || {}),
        listing_role: listingRole,
        status: failedStatus === "relist_failed" ? "relist_failed" : "update_failed",
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          `Failed to ${readyActionName(readyStatus)} ${listingRole} eBay quantity`,
      };
      setReconciliationByRowId((prev) => ({ ...prev, [key]: projection }));
      if (!options.silent) {
        setErrorMessage(projection.message);
      }
      onEbayErrorLogged();
      return projection;
    } finally {
      setRowAction("approving", rowId, listingRole, false);
    }
  };

  const applyAllReadyReconciliationRows = async () => {
    const readyActions = [];
    rows.forEach((row) => {
      ["single", "playset"].forEach((listingRole) => {
        const allocation =
          listingRole === "single"
            ? parseQuantity(row.single_allocation_quantity, 0)
            : parseQuantity(row.playset_allocation_quantity, 0);
        const key = reconciliationKey(row.id, listingRole);
        if (allocation > 0 && isReadyReconciliationStatus(reconciliationByRowId[key]?.status)) {
          readyActions.push({ rowId: row.id, listingRole });
        }
      });
    });
    if (readyActions.length === 0) {
      setErrorMessage("Refresh listing-role actions first; no ready eBay updates or relists are available");
      return;
    }
    setErrorMessage("");
    setStatusMessage("");
    setReconciliationActions((prev) => ({ ...prev, applyAll: true }));
    let successCount = 0;
    try {
      for (const action of readyActions) {
        const result = await approveReconciliationRow(action.rowId, action.listingRole, { silent: true });
        if (SUCCESS_RECONCILIATION_STATUSES.has(result?.status)) {
          successCount += 1;
        }
      }
      setStatusMessage(`Applied ${successCount} of ${readyActions.length} ready eBay listing-role actions`);
      await refreshSelectedBatch(selectedBatchId);
      onEbayErrorLogged();
    } finally {
      setReconciliationActions((prev) => ({ ...prev, applyAll: false }));
    }
  };

  return (
    <Box>
      <Stack spacing={2}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2}>
          <Box>
            <Typography variant="h6">Trainer Bulk Intake</Typography>
            <Typography variant="body2" color="text.secondary">
              Add new Trainer listing quantities quickly. Choose the incoming Single/Playset split, refresh the trusted current eBay quantity, then approve the proposed replenishment.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="outlined" onClick={() => refreshSelectedBatch()}>
              Refresh Batches
            </Button>
            <Button
              variant="outlined"
              onClick={finaliseBatch}
              disabled={!selectedBatchId || selectedBatch?.status === "finalised"}
            >
              Finalise Stock (Optional)
            </Button>
          </Stack>
        </Stack>

        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        {statusMessage && <Alert severity="success">{statusMessage}</Alert>}

        <Alert severity="info" icon={false}>
          Listing replenishment workflow: Save the incoming quantity split → Refresh trusted eBay Qty → Review Proposed Qty → Approve/Update. Finalise Stock is optional local bookkeeping and does not gate eBay replenishment.
        </Alert>

        <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>Batch Control</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl size="small" fullWidth>
                <InputLabel>Existing Batch</InputLabel>
                <Select
                  value={selectedBatchId || ""}
                  label="Existing Batch"
                  onChange={(e) => handleBatchSelect(e.target.value)}
                >
                  <MenuItem value="">Select batch</MenuItem>
                  {batches.map((batch) => (
                    <MenuItem key={batch.id} value={batch.id}>
                      {`#${batch.id} ${batch.batch_name} (${batch.status})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                size="small"
                fullWidth
                label="New Batch Name"
                value={batchDraft.batch_name}
                onChange={(e) => updateBatchDraft("batch_name", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                size="small"
                fullWidth
                label="Source Label"
                value={batchDraft.source_label}
                onChange={(e) => updateBatchDraft("source_label", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button fullWidth variant="contained" onClick={createBatch}>
                Create Batch
              </Button>
            </Grid>
            <Grid item xs={12}>
              <TextField
                size="small"
                fullWidth
                label="Batch Notes"
                value={batchDraft.notes}
                onChange={(e) => updateBatchDraft("notes", e.target.value)}
              />
            </Grid>
          </Grid>
        </Box>

        <Grid container spacing={1}>
          <Grid item xs={6} md={2}>
            <Alert severity="info" icon={false}>Rows {selectedBatch?.row_count ?? rowTotals.rows}</Alert>
          </Grid>
          <Grid item xs={6} md={2}>
            <Alert severity="info" icon={false}>Qty {selectedBatch?.total_quantity ?? rowTotals.quantity}</Alert>
          </Grid>
          <Grid item xs={6} md={2}>
            <Alert severity="success" icon={false}>Stock recorded {selectedBatch?.applied_quantity ?? rowTotals.applied}</Alert>
          </Grid>
          <Grid item xs={6} md={2}>
            <Alert severity="success" icon={false}>Matched {rowTotals.matched}</Alert>
          </Grid>
          <Grid item xs={6} md={2}>
            <Alert severity="warning" icon={false}>New {rowTotals.created}</Alert>
          </Grid>
          <Grid item xs={6} md={2}>
            <Alert severity={selectedBatch?.status === "finalised" ? "success" : "info"} icon={false}>
              {selectedBatch?.status || "No batch"}
            </Alert>
          </Grid>
        </Grid>

        <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>Manual Row Entry</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField
                  size="small"
                  fullWidth
                  label="Card Name"
                  value={rowDraft.card_name}
                  onChange={(e) => updateRowDraft("card_name", e.target.value)}
                />
                <Button variant="outlined" onClick={openLookup} sx={{ minWidth: 112 }}>
                  Find Card
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={6} md={1.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Variant / Finish</InputLabel>
                <Select
                  label="Variant / Finish"
                  value={rowDraft.card_variant || "Normal"}
                  onChange={(e) => updateRowDraft("card_variant", e.target.value)}
                >
                  {CARD_VARIANT_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField
                size="small"
                fullWidth
                type="number"
                label="Quantity Received"
                value={rowDraft.quantity}
                onChange={(e) => updateRowDraft("quantity", e.target.value)}
                inputProps={{ min: 1, step: 1 }}
              />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField
                size="small"
                fullWidth
                type="number"
                label="Singles +"
                value={rowDraft.single_allocation_quantity}
                onChange={(e) => updateRowDraft("single_allocation_quantity", e.target.value)}
                inputProps={{ min: 0, step: 1 }}
              />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField
                size="small"
                fullWidth
                type="number"
                label="Playsets +"
                value={rowDraft.playset_allocation_quantity}
                onChange={(e) => updateRowDraft("playset_allocation_quantity", e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                helperText="Each uses 4 cards"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button size="small" variant="outlined" onClick={() => applyRowAllocationMode("all_singles")}>All Singles</Button>
                <Button size="small" variant="outlined" onClick={() => applyRowAllocationMode("max_playsets")}>Max Playsets</Button>
                <Button size="small" variant="outlined" onClick={() => applyRowAllocationMode("playsets_with_remainder")}>Playsets + Singles Remainder</Button>
                <Button size="small" variant="outlined" onClick={() => applyRowAllocationMode("unallocated")}>Leave Unallocated</Button>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Alert severity={rowDraftAllocation.valid ? "info" : "error"} icon={false}>
                Allocated {rowDraftAllocation.allocated} / {rowDraftAllocation.quantity} physical cards: {rowDraftAllocation.singles} singles + {rowDraftAllocation.playsets} playsets x4. Unallocated: {rowDraftAllocation.unallocated}.
                {!rowDraftAllocation.valid ? " Allocation exceeds received quantity." : ""}
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <TrainerAllocationDecisionPanel
                context={allocationContext}
                loading={allocationContextLoading}
                error={allocationContextError}
                liveRefresh={allocationStoreQty}
                liveRefreshing={allocationStoreQtyRefreshing}
                onRefreshStoreQty={refreshAllocationStoreQty}
                onUseSuggestion={useAllocationSuggestion}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                size="small"
                fullWidth
                label="Condition"
                value={rowDraft.card_condition}
                onChange={(e) => updateRowDraft("card_condition", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2.5}>
              <FormControl size="small" fullWidth>
                <InputLabel>New Card Strategy</InputLabel>
                <Select
                  value={rowDraft.listing_strategy}
                  label="New Card Strategy"
                  onChange={(e) => updateRowDraft("listing_strategy", e.target.value)}
                >
                  {TRAINER_LISTING_STRATEGIES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Used only if this intake creates a new Trainer Inventory card. Existing strategy is preserved.
                </Typography>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Save Mode</InputLabel>
                <Select
                  value={rowDraft.inventory_apply_mode}
                  label="Save Mode"
                  onChange={(e) => updateRowDraft("inventory_apply_mode", e.target.value)}
                >
                  {APPLY_MODES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl size="small" fullWidth>
                <InputLabel>Source</InputLabel>
                <Select
                  value={rowDraft.source_type}
                  label="Source"
                  onChange={(e) => updateRowDraft("source_type", e.target.value)}
                >
                  {SOURCE_TYPES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                size="small"
                fullWidth
                type="number"
                label="Unit Cost"
                value={rowDraft.unit_cost}
                onChange={(e) => updateRowDraft("unit_cost", e.target.value)}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                size="small"
                fullWidth
                label="Storage"
                value={rowDraft.storage_location}
                onChange={(e) => updateRowDraft("storage_location", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                size="small"
                fullWidth
                label="Source Reference"
                value={rowDraft.source_reference}
                onChange={(e) => updateRowDraft("source_reference", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                size="small"
                fullWidth
                label="Notes"
                value={rowDraft.notes}
                onChange={(e) => updateRowDraft("notes", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button fullWidth variant="contained" onClick={saveSingleRow} disabled={!selectedBatchId || !rowDraftAllocation.valid}>
                Save Row
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>Paste Import</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <Stack spacing={1}>
                <TextField
                  fullWidth
                  multiline
                  minRows={8}
                  label="Card Name, Quantity OR Card Name, Quantity, Singles, Playsets"
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                />
                <FormControl size="small" fullWidth>
                  <InputLabel>2-column allocation default</InputLabel>
                  <Select
                    value={pasteAllocationMode}
                    label="2-column allocation default"
                    onChange={(e) => setPasteAllocationMode(e.target.value)}
                  >
                    <MenuItem value="all_singles">All Singles</MenuItem>
                    <MenuItem value="max_playsets">Max Playsets</MenuItem>
                    <MenuItem value="playsets_with_remainder">Playsets + Singles Remainder</MenuItem>
                    <MenuItem value="unallocated">Leave Unallocated</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Grid>
            <Grid item xs={12} md={7}>
              <Stack spacing={1}>
                <Alert severity="info" icon={false}>
                  Preview: {validPasteRows.length} valid of {parsedPasteRows.length} parsed lines. Four-column rows use explicit allocations; two-column rows use the selected allocation default. Other intake defaults come from the manual row controls.
                </Alert>
                <TableContainer sx={{ maxHeight: 260, border: 1, borderColor: "divider", borderRadius: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Card Name</TableCell>
                        <TableCell>Variant</TableCell>
                        <TableCell>Qty</TableCell>
                        <TableCell>Singles</TableCell>
                        <TableCell>Playsets</TableCell>
                        <TableCell>Allocated</TableCell>
                        <TableCell>Unallocated</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parsedPasteRows.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>{row.card_name || row.raw}</TableCell>
                          <TableCell>{rowDraft.card_variant || "Normal"}</TableCell>
                          <TableCell>{row.quantity || "-"}</TableCell>
                          <TableCell>{row.single_allocation_quantity ?? "-"}</TableCell>
                          <TableCell>{row.playset_allocation_quantity ?? "-"}</TableCell>
                          <TableCell>{row.allocated ?? "-"}</TableCell>
                          <TableCell>{row.unallocated ?? "-"}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              color={row.valid ? "success" : "error"}
                              label={row.valid ? "Ready" : "Check format"}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                      {parsedPasteRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8}>No pasted rows to preview.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Button
                  variant="contained"
                  onClick={savePasteRows}
                  disabled={!selectedBatchId || savingBulk || validPasteRows.length === 0 || pasteHasInvalidRows}
                >
                  {savingBulk ? "Saving Rows" : "Save Pasted Rows"}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <TrainerIntakeEbayReconciliationPanel
          rows={rows}
          reconciliationByRowId={reconciliationByRowId}
          selectedRowId={selectedRowId}
          actionStates={reconciliationActions}
          onSelectRow={setSelectedRowId}
          onRefreshRow={refreshReconciliationRow}
          onApproveRow={approveReconciliationRow}
          onApplyAllReady={applyAllReadyReconciliationRows}
        />

        <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1 }}>
          <Box sx={{ p: 2, pb: 0 }}>
            <Typography variant="subtitle1">Saved Batch Rows</Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Card Name</TableCell>
                  <TableCell>Variant</TableCell>
                  <TableCell align="right">Received</TableCell>
                  <TableCell align="right">Singles +</TableCell>
                  <TableCell align="right">Playsets +</TableCell>
                  <TableCell align="right">Allocated</TableCell>
                  <TableCell align="right">Unallocated</TableCell>
                  <TableCell>Condition</TableCell>
                  <TableCell>Inventory Strategy</TableCell>
                  <TableCell>Source / Cost</TableCell>
                  <TableCell>Inventory</TableCell>
                  <TableCell>Before → After</TableCell>
                  <TableCell>Local Stock (optional)</TableCell>
                  <TableCell>Listing Drafts</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const singleDraftStatus = singleDraftStatusByRowId[row.id];
                  const playsetDraftStatus = playsetDraftStatusByRowId[row.id];
                  const canCreateSingleDraft =
                    row.trainer_inventory_summary_id &&
                    row.inventory_applied_at &&
                    parseQuantity(row.single_allocation_quantity, 0) > 0 &&
                    !row.inventory_linked_listing_id;
                  const canCreatePlaysetDraft =
                    row.trainer_inventory_summary_id &&
                    row.inventory_applied_at &&
                    parseQuantity(row.playset_allocation_quantity, 0) > 0 &&
                    !row.inventory_linked_playset_listing_id;
                  return (
                    <TableRow
                      key={row.id}
                      hover
                      selected={String(row.id) === String(selectedRowId)}
                      onClick={() => setSelectedRowId(row.id)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>{row.card_name}</TableCell>
                      <TableCell>{row.card_variant || row.inventory_card_variant || "Normal"}</TableCell>
                      <TableCell align="right">{row.quantity}</TableCell>
                      <TableCell align="right">+{row.single_allocation_quantity || 0}</TableCell>
                      <TableCell align="right">+{row.playset_allocation_quantity || 0}</TableCell>
                      <TableCell align="right">{row.allocated_physical_units ?? 0}</TableCell>
                      <TableCell align="right">{row.unallocated_quantity ?? 0}</TableCell>
                      <TableCell>{row.card_condition || "-"}</TableCell>
                      <TableCell>{row.inventory_listing_strategy || row.listing_strategy}</TableCell>
                      <TableCell>{row.source_type} / {row.unit_cost ?? 0}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip
                            size="small"
                            color={row.match_status === "created_new" ? "warning" : "success"}
                            label={statusLabel(row)}
                          />
                          <Typography variant="caption">
                            #{row.trainer_inventory_summary_id}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography variant="caption">On hand {row.inventory_quantity_on_hand_before_intake ?? 0} → {row.inventory_quantity_on_hand ?? 0}</Typography>
                          <Typography variant="caption">Singles {row.inventory_target_single_quantity_before_intake ?? 0} → {row.inventory_target_single_quantity ?? 0}</Typography>
                          <Typography variant="caption">Playsets {row.inventory_target_playset_quantity_before_intake ?? 0} → {row.inventory_target_playset_quantity ?? 0}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.5} alignItems="flex-start">
                          <Chip
                            size="small"
                            color={row.inventory_applied_at ? "success" : "default"}
                            label={row.inventory_applied_at ? `Stock received ${row.quantity_applied}` : "Stock staged"}
                          />
                          {row.allocation_applied_at && (
                            <Chip size="small" variant="outlined" label="Historic local allocation applied" />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.75}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            {row.inventory_linked_listing_id ? (
                              <Chip size="small" color="success" label={`Single linked #${row.inventory_linked_listing_id}`} />
                            ) : canCreateSingleDraft ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={(event) => createSingleDraftForRow(row, event)}
                                disabled={singleDraftStatus === "saving"}
                              >
                                {singleDraftStatus === "saving" ? "Creating" : "Create Single Draft"}
                              </Button>
                            ) : parseQuantity(row.single_allocation_quantity, 0) > 0 ? (
                              <Chip size="small" label={row.inventory_applied_at ? "Single listing missing" : "Single draft needs stock finalise"} />
                            ) : null}
                            {singleDraftStatus === "saved" && <Chip size="small" color="success" label="Single draft ready" />}
                            {singleDraftStatus && !["saving", "saved"].includes(singleDraftStatus) && (
                              <Chip size="small" color="error" label={String(singleDraftStatus).slice(0, 28)} />
                            )}
                          </Stack>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            {row.inventory_linked_playset_listing_id ? (
                              <Chip size="small" color="success" label={`Playset linked #${row.inventory_linked_playset_listing_id}`} />
                            ) : canCreatePlaysetDraft ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={(event) => createPlaysetDraftForRow(row, event)}
                                disabled={playsetDraftStatus === "saving"}
                              >
                                {playsetDraftStatus === "saving" ? "Creating" : "Create Playset Draft"}
                              </Button>
                            ) : parseQuantity(row.playset_allocation_quantity, 0) > 0 ? (
                              <Chip size="small" label={row.inventory_applied_at ? "Playset listing missing" : "Playset draft needs stock finalise"} />
                            ) : null}
                            {playsetDraftStatus === "saved" && <Chip size="small" color="success" label="Playset draft ready" />}
                            {playsetDraftStatus && !["saving", "saved"].includes(playsetDraftStatus) && (
                              <Chip size="small" color="error" label={String(playsetDraftStatus).slice(0, 28)} />
                            )}
                          </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell>{row.notes || "-"}</TableCell>
                    </TableRow>
                  );
                })}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={15}>
                      {selectedBatchId ? "No rows have been saved for this batch." : "Select or create a batch."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Dialog open={lookupOpen} onClose={closeLookup} fullWidth maxWidth="md">
          <DialogTitle>Find Card</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField
                autoFocus
                fullWidth
                label="Search card name, set, or number"
                value={lookupQuery}
                onChange={(e) => setLookupQuery(e.target.value)}
              />
              {lookupError && <Alert severity="error">{lookupError}</Alert>}
              {lookupLoading && (
                <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              )}
              {!lookupLoading && lookupQuery.trim().length >= 2 && lookupResults.length === 0 && !lookupError && (
                <Alert severity="info">No cards found for this search.</Alert>
              )}
              {!lookupLoading && lookupResults.length > 0 && (
                <TableContainer sx={{ maxHeight: 420, border: 1, borderColor: "divider", borderRadius: 1 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Card</TableCell>
                        <TableCell>Set</TableCell>
                        <TableCell>Rarity</TableCell>
                        <TableCell align="right">Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lookupResults.map((card) => (
                        <TableRow key={card.id || card.name_and_number}>
                          <TableCell>{card.name_and_number}</TableCell>
                          <TableCell>{card.set_name || "-"}</TableCell>
                          <TableCell>{card.rarity || "-"}</TableCell>
                          <TableCell align="right">
                            <Button size="small" variant="contained" onClick={() => selectLookupCard(card)}>
                              Select
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeLookup}>Cancel</Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
}
